import { apiClient } from "@/shared/http";
import type { IntakeMembership } from "../domain/authSession";

/**
 * Intake BE(spnp) `GET /v1/me` 경계.
 *
 * Owns:
 * - 응답 모양 확인과 `IntakeMembership` 변환. 서버는 organization · member · identity 를 주지만 세션이 쓰는
 *   것은 조직 uuid·name 과 member uuid 뿐이라 그것만 옮긴다(identity 는 Overlord 로그인 응답으로 이미 있다).
 * - 모양이 계약과 다르면 성공으로 보지 않는다 — 필수 필드가 비면 throw.
 *
 * Does not own:
 * - 401/403/네트워크 실패의 의미(`feature/intakeMembership.ts`), Bearer 첨부와 세션 무효화(`apiClient`).
 */
export async function getCurrentMe(): Promise<IntakeMembership> {
  const response = await apiClient.get<unknown>("/v1/me");
  const membership = parseMeResp(response.data);
  if (membership === null) {
    throw new Error("Unexpected /v1/me response shape.");
  }

  return membership;
}

// 서버 응답은 외부 입력으로 취급한다 — 필요한 필드가 하나라도 비면 계약 위반이다.
function parseMeResp(value: unknown): IntakeMembership | null {
  if (!isRecord(value) || !isRecord(value.organization) || !isRecord(value.member)) {
    return null;
  }

  const organizationUuid = nonEmptyString(value.organization.uuid);
  const organizationName = nonEmptyString(value.organization.name);
  const memberUuid = nonEmptyString(value.member.uuid);
  if (organizationUuid === null || organizationName === null || memberUuid === null) {
    return null;
  }

  return { organizationUuid, organizationName, memberUuid };
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
